<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { MarkerType } from '@vue-flow/core';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';

import { useOnixFlowsStore } from 'dashboard/store/onix/flows';
import { useOnixActionsStore } from 'dashboard/store/onix/actions';
import { useOnixKnowledgeStore } from 'dashboard/store/onix/knowledge';
import OnixFlowInboxesAPI from 'dashboard/api/onix/flowInboxes';
import Button from 'dashboard/components-next/button/Button.vue';
import FlowCanvas from 'dashboard/components-next/onix/FlowCanvas.vue';

const { t } = useI18n();
const route = useRoute();
const store = useStore();
const { isAdmin } = useAdmin();
const flowsStore = useOnixFlowsStore();
const actionsStore = useOnixActionsStore();
const knowledgeStore = useOnixKnowledgeStore();
const accountInboxes = useMapGetter('inboxes/getInboxes');
const accountLabels = useMapGetter('labels/getLabels');
const accountTeams = useMapGetter('teams/getTeams');

const emptyEffects = () => ({
  labelTitles: [],
  teamId: '',
  priority: '',
  privateNote: '',
  resolve: false,
  handoff: false,
  allowModel: false,
  allowLabels: [],
  allowTeamIds: [],
});

const flowId = computed(() => route.params.flowId);
const nodes = ref([]);
const edges = ref([]);
const selectedNodeId = ref(null);
const selectedEdgeId = ref(null);
const dirty = ref(false);

const flowName = ref('');
const flowEnabled = ref(false);
const entryNodeKey = ref('');

const draftKey = ref('');
const draftType = ref('respond');
const draftInstruction = ref('');
const draftGuardrails = ref('');
const draftBranches = ref([]);
const draftDefaultBranch = ref('default');
const draftCollectionId = ref('');
const draftTopK = ref(5);
const draftActionIds = ref([]);
const draftMaxRounds = ref(3);
const draftContent = ref('');
const draftAwait = ref(false);
const draftResumeNode = ref('');
const draftOperation = ref('detail');
const draftEffects = ref(emptyEffects());

const boundInboxes = ref([]);
const inboxToAdd = ref('');
const inboxError = ref('');
const inboxBusy = ref(false);

const NODE_TYPES = [
  'classify',
  'instruct',
  'retrieve',
  'agent',
  'condition',
  'respond',
  'handoff',
  'extract',
  'actibid',
  'compute',
  'assign',
  'step_up',
];

const fieldClass =
  'w-full h-9 px-3 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12';
const areaClass =
  'w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12';

const selectedNode = computed(() =>
  nodes.value.find(n => n.id === selectedNodeId.value)
);
const selectedEdge = computed(() =>
  edges.value.find(e => e.id === selectedEdgeId.value)
);
const verifiedActions = computed(() => actionsStore.getVerifiedActions);
const collections = computed(() => knowledgeStore.getCollections);
const otherNodes = computed(() =>
  nodes.value.filter(n => n.id !== selectedNodeId.value)
);

const humanize = value =>
  String(value || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\p{L}/gu, char => char.toLocaleUpperCase('es'));

const slugify = value =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const typeLabel = type =>
  t(`ONIX.CANVAS.TYPE_${String(type || 'respond').toUpperCase()}`);

const nodeLabel = node => {
  const type = node.data?.node_type || node.node_type || 'respond';
  const config = node.data?.config || node.config || {};
  const title = String(config.title || '').trim();
  if (title) return title;
  const base = typeLabel(type);
  if (type === 'instruct') {
    const text = String(config.instruction || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return base;
    return text.length > 56 ? `${text.slice(0, 56)}…` : text;
  }
  if (type === 'classify') {
    const names = (config.branches || [])
      .map(branch => humanize(branch.key))
      .filter(Boolean);
    return names.length ? `${base}: ${names.join(', ')}` : base;
  }
  if (type === 'retrieve') {
    const found = collections.value.find(
      item => Number(item.id) === Number(config.collection_id)
    );
    return found ? `${base}: ${found.name}` : base;
  }
  return base;
};

const labelById = id => nodes.value.find(node => node.id === id)?.label || '';

const edgeEnds = edge =>
  `${labelById(edge?.source)} → ${labelById(edge?.target)}`;

const availableInboxes = computed(() => {
  const bound = new Set(boundInboxes.value.map(row => row.inbox_id));
  return (accountInboxes.value || []).filter(inbox => !bound.has(inbox.id));
});

const helpKey = computed(() => {
  const type = draftType.value || 'respond';
  return `ONIX.CANVAS.HELP_${type.toUpperCase()}`;
});

const edgeLabel = condition => {
  if (condition?.default === true) return t('ONIX.CANVAS.EDGE_DEFAULT');
  if (condition?.outcome) return humanize(condition.outcome);
  return t('ONIX.CANVAS.EDGE_UNSET');
};

const mapNodes = list =>
  (list || []).map(n => ({
    id: n.key,
    type: 'default',
    position: { x: n.position_x || 0, y: n.position_y || 0 },
    label: nodeLabel({
      data: { node_type: n.node_type, config: n.config || {} },
    }),
    data: {
      key: n.key,
      node_type: n.node_type,
      config: n.config || {},
      dbId: n.id,
    },
  }));

const mapEdges = list =>
  (list || []).map(e => {
    const condition = e.condition || {};
    return {
      id: `e-${e.id || `${e.source_key}-${e.target_key}`}`,
      source: e.source_key,
      target: e.target_key,
      label: edgeLabel(condition),
      markerEnd: MarkerType.ArrowClosed,
      data: { condition },
    };
  });

const applyGraph = data => {
  if (!data) return;
  flowName.value = data.name || '';
  flowEnabled.value = !!data.enabled;
  entryNodeKey.value = data.entry_node_key || '';
  nodes.value = mapNodes(data.nodes);
  edges.value = mapEdges(data.edges);
};

const loadInboxes = async () => {
  if (!flowId.value) return;
  try {
    const { data } = await OnixFlowInboxesAPI.get(flowId.value);
    boundInboxes.value = data.payload || [];
  } catch (error) {
    inboxError.value =
      error?.response?.data?.message || t('ONIX.CANVAS.INBOX_ERROR');
  }
};

const loadFlow = async () => {
  const data = await flowsStore.show(flowId.value);
  if (!data) return;
  applyGraph(data);
  selectedNodeId.value = null;
  selectedEdgeId.value = null;
  dirty.value = false;
  await loadInboxes();
};

const fillDraft = node => {
  const config = node.data?.config || {};
  draftKey.value = node.data?.key || node.id;
  draftType.value = node.data?.node_type || 'respond';
  draftInstruction.value = config.instruction || '';
  draftGuardrails.value = Array.isArray(config.guardrails)
    ? config.guardrails.join('\n')
    : '';
  const branches = Array.isArray(config.branches) ? config.branches : [];
  draftBranches.value = branches.length
    ? branches.map(branch => ({
        key: branch.key || '',
        name: humanize(branch.key),
        keywordsText: Array.isArray(branch.keywords)
          ? branch.keywords.join(', ')
          : '',
      }))
    : [{ key: '', name: '', keywordsText: '' }];
  draftDefaultBranch.value = config.default_branch || 'default';
  draftCollectionId.value = config.collection_id || '';
  draftTopK.value = config.top_k || 5;
  draftActionIds.value = Array.isArray(config.action_ids)
    ? config.action_ids.map(id => Number(id))
    : [];
  draftMaxRounds.value = config.max_tool_rounds || 3;
  draftContent.value = config.content || '';
  draftAwait.value = !!config.await_next_message;
  draftResumeNode.value = config.resume_node_key || '';
  draftOperation.value = config.operation || 'detail';
  const effects = config.effects || {};
  const allow = effects.allow_model || {};
  draftEffects.value = {
    labelTitles: Array.isArray(effects.label_titles)
      ? [...effects.label_titles]
      : [],
    teamId: effects.team_id ? String(effects.team_id) : '',
    priority: effects.priority || '',
    privateNote: effects.private_note || '',
    resolve: !!effects.resolve,
    handoff: !!effects.handoff,
    allowModel: effects.allow_model != null,
    allowLabels: Array.isArray(allow.labels) ? [...allow.labels] : [],
    allowTeamIds: Array.isArray(allow.team_ids)
      ? allow.team_ids.map(id => Number(id))
      : [],
  };
};

onMounted(async () => {
  if (!accountInboxes.value?.length) store.dispatch('inboxes/get');
  if (!accountLabels.value?.length) store.dispatch('labels/get');
  if (!accountTeams.value?.length) store.dispatch('teams/get');
  await Promise.all([actionsStore.fetch(), knowledgeStore.fetch(), loadFlow()]);
});

watch(flowId, () => loadFlow());

watch(collections, () => {
  nodes.value = nodes.value.map(node => ({
    ...node,
    label: nodeLabel(node),
  }));
});

const onConnect = params => {
  const hasDefault = edges.value.some(
    edge =>
      edge.source === params.source && edge.data?.condition?.default === true
  );
  const condition = hasDefault ? { outcome: '' } : { default: true };
  edges.value = [
    ...edges.value,
    {
      ...params,
      id: `e-${params.source}-${params.target}-${Date.now()}`,
      label: edgeLabel(condition),
      markerEnd: MarkerType.ArrowClosed,
      data: { condition },
    },
  ];
  dirty.value = true;
};

const onNodeClick = node => {
  selectedEdgeId.value = null;
  selectedNodeId.value = node.id;
  fillDraft(node);
};

const onEdgeClick = edge => {
  selectedNodeId.value = null;
  selectedEdgeId.value = edge.id;
};

const clearSelection = () => {
  selectedNodeId.value = null;
  selectedEdgeId.value = null;
};

const effectsConfig = ({ includeAllowModel = false } = {}) => {
  const effects = {
    label_titles: draftEffects.value.labelTitles,
    team_id: draftEffects.value.teamId
      ? Number(draftEffects.value.teamId)
      : null,
    priority: draftEffects.value.priority || '',
    private_note: draftEffects.value.privateNote.trim(),
    resolve: draftEffects.value.resolve,
    handoff: draftEffects.value.handoff,
  };
  if (includeAllowModel && draftEffects.value.allowModel) {
    effects.allow_model = {
      labels: draftEffects.value.allowLabels,
      team_ids: draftEffects.value.allowTeamIds.map(id => Number(id)),
    };
  }
  return effects;
};

const buildConfig = type => {
  if (type === 'instruct') {
    return {
      instruction: draftInstruction.value.trim(),
      guardrails: draftGuardrails.value
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean),
      effects: effectsConfig({ includeAllowModel: true }),
    };
  }
  if (type === 'classify') {
    return {
      branches: draftBranches.value
        .filter(branch => (branch.name || branch.key).trim())
        .map(branch => ({
          key: slugify(branch.name) || branch.key,
          keywords: branch.keywordsText
            .split(',')
            .map(word => word.trim())
            .filter(Boolean),
        })),
      default_branch: draftDefaultBranch.value.trim() || 'default',
    };
  }
  if (type === 'retrieve') {
    const collectionId = Number(draftCollectionId.value);
    return {
      collection_id: collectionId > 0 ? collectionId : null,
      top_k: Number(draftTopK.value) || 5,
    };
  }
  if (type === 'agent') {
    return {
      action_ids: draftActionIds.value.map(id => Number(id)),
      max_tool_rounds: Number(draftMaxRounds.value) || 3,
    };
  }
  if (type === 'respond') {
    return {
      content: draftContent.value.trim(),
      await_next_message: draftAwait.value,
      resume_node_key: draftAwait.value ? draftResumeNode.value : null,
      effects: effectsConfig(),
    };
  }
  if (type === 'handoff') {
    return { effects: effectsConfig() };
  }
  if (type === 'actibid') {
    return { operation: draftOperation.value || 'detail' };
  }
  if (type === 'compute') {
    return { operator: 'sae' };
  }
  return {};
};

const showsEffects = computed(() =>
  ['instruct', 'respond', 'handoff'].includes(draftType.value)
);

const toggleIn = (list, value) => {
  const next = list.includes(value)
    ? list.filter(item => item !== value)
    : [...list, value];
  return next;
};

const patchEdge = (edgeId, recipe) => {
  edges.value = edges.value.map(edge =>
    edge.id === edgeId ? recipe(edge) : edge
  );
  dirty.value = true;
};

const renameBranchOutcome = (branch, previousKey) => {
  const next = branch.key.trim();
  const prev = (previousKey || '').trim();
  if (!prev || prev === next || !selectedNodeId.value) return;
  edges.value = edges.value.map(edge => {
    if (edge.source !== selectedNodeId.value) return edge;
    if (edge.data?.condition?.outcome !== prev) return edge;
    const condition = { ...edge.data.condition, outcome: next };
    return {
      ...edge,
      data: { ...edge.data, condition },
      label: edgeLabel(condition),
    };
  });
  dirty.value = true;
};

const applyNodeEdits = () => {
  const node = selectedNode.value;
  if (!node) return;

  const config = buildConfig(draftType.value);
  const title = node.data?.config?.title;
  if (title) config.title = title;
  if (draftType.value === 'classify') {
    (config.branches || []).forEach((branch, index) => {
      const previous = draftBranches.value[index]?.key;
      if (previous && branch.key && previous !== branch.key) {
        renameBranchOutcome({ key: branch.key }, previous);
      }
    });
  }
  const oldId = node.id;
  const newId = draftKey.value.trim() || oldId;

  nodes.value = nodes.value.map(item => {
    if (item.id !== oldId) return item;
    return {
      ...item,
      id: newId,
      label: nodeLabel({
        data: { node_type: draftType.value, config },
      }),
      data: {
        ...item.data,
        key: newId,
        node_type: draftType.value,
        config,
      },
    };
  });

  if (oldId !== newId) {
    edges.value = edges.value.map(edge => ({
      ...edge,
      source: edge.source === oldId ? newId : edge.source,
      target: edge.target === oldId ? newId : edge.target,
    }));
    if (entryNodeKey.value === oldId) entryNodeKey.value = newId;
    selectedNodeId.value = newId;
  }

  dirty.value = true;
};

const addNode = () => {
  const key = `node_${Date.now()}`;
  nodes.value = [
    ...nodes.value,
    {
      id: key,
      type: 'default',
      position: {
        x: 80 + nodes.value.length * 40,
        y: 80 + nodes.value.length * 30,
      },
      label: nodeLabel({ data: { node_type: 'respond', config: {} } }),
      data: { key, node_type: 'respond', config: {} },
    },
  ];
  dirty.value = true;
};

const deleteSelectedNode = () => {
  const id = selectedNodeId.value;
  if (!id) return;
  nodes.value = nodes.value.filter(node => node.id !== id);
  edges.value = edges.value.filter(
    edge => edge.source !== id && edge.target !== id
  );
  if (entryNodeKey.value === id) {
    entryNodeKey.value = nodes.value[0]?.id || '';
  }
  selectedNodeId.value = null;
  dirty.value = true;
};

const deleteSelectedEdge = () => {
  const id = selectedEdgeId.value;
  if (!id) return;
  edges.value = edges.value.filter(edge => edge.id !== id);
  selectedEdgeId.value = null;
  dirty.value = true;
};

const branchTarget = branchKey => {
  const edge = edges.value.find(
    item =>
      item.source === selectedNodeId.value &&
      item.data?.condition?.outcome === branchKey
  );
  return edge?.target || '';
};

const setBranchTarget = (branchKey, targetId) => {
  const source = selectedNodeId.value;
  if (!source || !branchKey) return;
  const rest = edges.value.filter(
    edge =>
      !(edge.source === source && edge.data?.condition?.outcome === branchKey)
  );
  if (!targetId) {
    edges.value = rest;
    dirty.value = true;
    return;
  }
  const condition = { outcome: branchKey };
  edges.value = [
    ...rest,
    {
      id: `e-${source}-${targetId}-${branchKey}`,
      source,
      target: targetId,
      label: edgeLabel(condition),
      markerEnd: MarkerType.ArrowClosed,
      data: { condition },
    },
  ];
  dirty.value = true;
};

const addBranch = () => {
  draftBranches.value = [
    ...draftBranches.value,
    { key: '', name: '', keywordsText: '' },
  ];
};

const removeBranch = index => {
  draftBranches.value = draftBranches.value.filter((_, i) => i !== index);
};

const toggleAction = actionId => {
  const id = Number(actionId);
  if (draftActionIds.value.includes(id)) {
    draftActionIds.value = draftActionIds.value.filter(item => item !== id);
  } else {
    draftActionIds.value = [...draftActionIds.value, id];
  }
};

const setEdgeTarget = targetId => {
  const edge = selectedEdge.value;
  if (!edge || !targetId) return;
  patchEdge(edge.id, current => ({ ...current, target: targetId }));
};

const setEdgeMode = mode => {
  const edge = selectedEdge.value;
  if (!edge) return;
  patchEdge(edge.id, current => {
    const condition =
      mode === 'default'
        ? { default: true }
        : { outcome: current.data?.condition?.outcome || '' };
    return {
      ...current,
      data: { ...current.data, condition },
      label: edgeLabel(condition),
    };
  });
};

const setEdgeOutcome = value => {
  const edge = selectedEdge.value;
  if (!edge) return;
  patchEdge(edge.id, current => {
    const condition = { outcome: value };
    return {
      ...current,
      data: { ...current.data, condition },
      label: edgeLabel(condition),
    };
  });
};

const outcomeChoices = computed(() => {
  const source = nodes.value.find(
    node => node.id === selectedEdge.value?.source
  );
  const branches = source?.data?.config?.branches;
  if (!Array.isArray(branches)) return [];
  return branches.map(branch => branch.key).filter(Boolean);
});

const saveFlow = async () => {
  if (selectedNode.value) applyNodeEdits();
  const flow = flowsStore.currentFlow;
  if (!flow) return;

  const payload = {
    flow: {
      name: flowName.value.trim() || flow.name,
      entry_node_key: entryNodeKey.value,
      enabled: flowEnabled.value,
      config: flow.config || {},
      nodes: nodes.value.map(node => ({
        key: node.data?.key || node.id,
        node_type: node.data?.node_type || 'respond',
        config: node.data?.config || {},
        position_x: Math.round(node.position?.x || 0),
        position_y: Math.round(node.position?.y || 0),
      })),
      edges: edges.value.map(edge => ({
        source_key: edge.source,
        target_key: edge.target,
        condition: edge.data?.condition || {},
      })),
    },
  };

  const data = await flowsStore.update(flow.id, payload);
  if (data) {
    applyGraph(data);
    dirty.value = false;
  }
};

const bindInbox = async () => {
  if (!inboxToAdd.value) return;
  inboxError.value = '';
  inboxBusy.value = true;
  try {
    await OnixFlowInboxesAPI.create(flowId.value, {
      inbox: { inbox_id: inboxToAdd.value, enabled: true },
    });
    inboxToAdd.value = '';
    await loadInboxes();
  } catch (error) {
    inboxError.value =
      error?.response?.data?.message || t('ONIX.CANVAS.INBOX_ERROR');
  } finally {
    inboxBusy.value = false;
  }
};

const toggleInbox = async row => {
  inboxError.value = '';
  inboxBusy.value = true;
  try {
    await OnixFlowInboxesAPI.update(flowId.value, row.inbox_id, {
      flow_inbox: { enabled: !row.enabled },
    });
    await loadInboxes();
  } catch (error) {
    inboxError.value =
      error?.response?.data?.message || t('ONIX.CANVAS.INBOX_ERROR');
  } finally {
    inboxBusy.value = false;
  }
};

const unbindInbox = async row => {
  inboxError.value = '';
  inboxBusy.value = true;
  try {
    await OnixFlowInboxesAPI.delete(flowId.value, row.inbox_id);
    await loadInboxes();
  } catch (error) {
    inboxError.value =
      error?.response?.data?.message || t('ONIX.CANVAS.INBOX_ERROR');
  } finally {
    inboxBusy.value = false;
  }
};
</script>

<template>
  <div class="flex w-full h-full min-h-0">
    <div class="flex flex-col flex-1 min-w-0 min-h-0">
      <div
        class="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-n-weak"
      >
        <div class="flex flex-wrap items-center gap-2 min-w-0">
          <input
            v-model="flowName"
            type="text"
            class="h-9 w-56 px-3 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12"
            :disabled="!isAdmin"
            @input="dirty = true"
          />
          <label class="flex items-center gap-2 text-sm text-n-slate-12">
            <input
              v-model="flowEnabled"
              type="checkbox"
              class="rounded border-n-weak"
              :disabled="!isAdmin"
              @change="dirty = true"
            />
            {{
              flowEnabled ? t('ONIX.FLOWS.ENABLED') : t('ONIX.FLOWS.DISABLED')
            }}
          </label>
          <label class="flex items-center gap-2 text-xs text-n-slate-11">
            {{ t('ONIX.CANVAS.ENTRY_NODE') }}
            <select
              v-model="entryNodeKey"
              class="h-9 px-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12"
              :disabled="!isAdmin"
              @change="dirty = true"
            >
              <option v-for="node in nodes" :key="node.id" :value="node.id">
                {{ node.label }}
              </option>
            </select>
          </label>
        </div>
        <div class="flex items-center gap-2">
          <Button
            :label="t('ONIX.CANVAS.ADD_NODE')"
            size="sm"
            color="slate"
            :disabled="!isAdmin"
            @click="addNode"
          />
          <Button
            :label="t('ONIX.CANVAS.SAVE')"
            size="sm"
            :is-loading="flowsStore.uiFlags.updatingItem"
            :disabled="!dirty || !isAdmin"
            @click="saveFlow"
          />
        </div>
      </div>
      <p class="px-4 py-2 text-xs text-n-slate-11 border-b border-n-weak">
        {{ t('ONIX.CANVAS.HINT') }}
      </p>

      <FlowCanvas
        v-model:nodes="nodes"
        v-model:edges="edges"
        class="flex-1 min-h-0"
        @node-click="onNodeClick"
        @edge-click="onEdgeClick"
        @pane-click="clearSelection"
        @node-drag-stop="dirty = true"
        @connect="onConnect"
      />
    </div>

    <aside
      class="w-96 shrink-0 border-l border-n-weak bg-n-solid-1 p-4 overflow-auto flex flex-col gap-6"
    >
      <section>
        <h2 class="text-sm font-semibold text-n-slate-12 mb-1">
          {{ t('ONIX.CANVAS.INBOXES') }}
        </h2>
        <p class="text-xs text-n-slate-11 mb-3">
          {{ t('ONIX.CANVAS.CHANNEL_HINT') }}
        </p>
        <ul class="m-0 p-0 list-none flex flex-col gap-2 mb-3">
          <li
            v-for="row in boundInboxes"
            :key="row.id"
            class="flex items-center justify-between gap-2 px-2 py-2 rounded-lg bg-n-alpha-1"
          >
            <div class="min-w-0">
              <div class="text-sm text-n-slate-12 truncate">
                {{ row.name }}
              </div>
              <div class="text-xs text-n-slate-11">
                {{
                  row.enabled
                    ? t('ONIX.FLOWS.ENABLED')
                    : t('ONIX.FLOWS.DISABLED')
                }}
              </div>
            </div>
            <div class="flex items-center gap-1 shrink-0">
              <Button
                :label="
                  row.enabled
                    ? t('ONIX.CANVAS.DISABLE_INBOX')
                    : t('ONIX.CANVAS.ENABLE_INBOX')
                "
                size="sm"
                color="slate"
                variant="faded"
                :disabled="!isAdmin || inboxBusy"
                @click="toggleInbox(row)"
              />
              <Button
                :label="t('ONIX.CANVAS.UNBIND')"
                size="sm"
                color="ruby"
                variant="faded"
                :disabled="!isAdmin || inboxBusy"
                @click="unbindInbox(row)"
              />
            </div>
          </li>
          <li v-if="!boundInboxes.length" class="text-xs text-n-slate-11">
            {{ t('ONIX.CANVAS.NO_INBOXES') }}
          </li>
        </ul>
        <div class="flex gap-2">
          <select v-model="inboxToAdd" :class="fieldClass" :disabled="!isAdmin">
            <option value="">
              {{ t('ONIX.CANVAS.PICK_INBOX') }}
            </option>
            <option
              v-for="inbox in availableInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
          <Button
            :label="t('ONIX.CANVAS.BIND')"
            size="sm"
            :disabled="!isAdmin || !inboxToAdd"
            :is-loading="inboxBusy"
            @click="bindInbox"
          />
        </div>
        <p v-if="inboxError" class="mt-2 text-xs text-n-ruby-11">
          {{ inboxError }}
        </p>
      </section>

      <section v-if="selectedEdge">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
          {{ t('ONIX.CANVAS.EDGE_PANEL') }}
        </h2>
        <p class="text-xs text-n-slate-11 mb-3">
          {{ edgeEnds(selectedEdge) }}
        </p>
        <label class="block text-xs text-n-slate-11 mb-1">
          {{ t('ONIX.CANVAS.EDGE_TARGET') }}
        </label>
        <select
          :value="selectedEdge.target"
          class="mb-3"
          :class="[fieldClass]"
          :disabled="!isAdmin"
          @change="setEdgeTarget($event.target.value)"
        >
          <option
            v-for="node in nodes.filter(n => n.id !== selectedEdge.source)"
            :key="node.id"
            :value="node.id"
          >
            {{ node.label }}
          </option>
        </select>
        <label class="block text-xs text-n-slate-11 mb-1">
          {{ t('ONIX.CANVAS.EDGE_MODE') }}
        </label>
        <select
          :value="selectedEdge.data?.condition?.default ? 'default' : 'outcome'"
          class="mb-3"
          :class="[fieldClass]"
          :disabled="!isAdmin"
          @change="setEdgeMode($event.target.value)"
        >
          <option value="default">
            {{ t('ONIX.CANVAS.EDGE_DEFAULT') }}
          </option>
          <option value="outcome">
            {{ t('ONIX.CANVAS.EDGE_OUTCOME') }}
          </option>
        </select>
        <template v-if="!selectedEdge.data?.condition?.default">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.EDGE_OUTCOME') }}
          </label>
          <input
            v-if="!outcomeChoices.length"
            :value="selectedEdge.data?.condition?.outcome || ''"
            type="text"
            class="mb-3"
            :class="[fieldClass]"
            :disabled="!isAdmin"
            @change="setEdgeOutcome($event.target.value)"
          />
          <select
            v-else
            :value="selectedEdge.data?.condition?.outcome || ''"
            class="mb-3"
            :class="[fieldClass]"
            :disabled="!isAdmin"
            @change="setEdgeOutcome($event.target.value)"
          >
            <option value="">
              {{ t('ONIX.CANVAS.EDGE_UNSET') }}
            </option>
            <option
              v-for="choice in outcomeChoices"
              :key="choice"
              :value="choice"
            >
              {{ humanize(choice) }}
            </option>
          </select>
        </template>
        <Button
          :label="t('ONIX.CANVAS.DELETE_EDGE')"
          size="sm"
          color="ruby"
          variant="faded"
          class="w-full"
          :disabled="!isAdmin"
          @click="deleteSelectedEdge"
        />
      </section>

      <section v-else-if="selectedNode">
        <h2 class="text-sm font-semibold text-n-slate-12 mb-3">
          {{ t('ONIX.CANVAS.NODE_PANEL') }}
        </h2>
        <p class="text-xs text-n-slate-11 mb-3">
          {{ t(helpKey) }}
        </p>

        <label class="block text-xs text-n-slate-11 mb-1">
          {{ t('ONIX.CANVAS.NODE_TYPE') }}
        </label>
        <select
          v-model="draftType"
          class="mb-3"
          :class="[fieldClass]"
          :disabled="!isAdmin"
        >
          <option v-for="type in NODE_TYPES" :key="type" :value="type">
            {{ t(`ONIX.CANVAS.TYPE_${type.toUpperCase()}`) }}
          </option>
        </select>

        <template v-if="draftType === 'classify'">
          <div
            v-for="(branch, index) in draftBranches"
            :key="index"
            class="mb-3 p-2 rounded-lg border border-n-weak"
          >
            <label class="block text-xs text-n-slate-11 mb-1">
              {{ t('ONIX.CANVAS.BRANCH_NAME') }}
            </label>
            <input
              v-model="branch.name"
              type="text"
              class="mb-2"
              :class="[fieldClass]"
              :disabled="!isAdmin"
              :placeholder="t('ONIX.CANVAS.BRANCH_NAME_PLACEHOLDER')"
            />
            <label class="block text-xs text-n-slate-11 mb-1">
              {{ t('ONIX.CANVAS.BRANCH_KEYWORDS') }}
            </label>
            <input
              v-model="branch.keywordsText"
              type="text"
              class="mb-2"
              :class="[fieldClass]"
              :disabled="!isAdmin"
              :placeholder="t('ONIX.CANVAS.KEYWORDS_PLACEHOLDER')"
            />
            <label class="block text-xs text-n-slate-11 mb-1">
              {{ t('ONIX.CANVAS.CONNECT_TO') }}
            </label>
            <select
              :value="branchTarget(slugify(branch.name) || branch.key)"
              class="mb-2"
              :class="[fieldClass]"
              :disabled="!isAdmin || !(slugify(branch.name) || branch.key)"
              @change="
                setBranchTarget(
                  slugify(branch.name) || branch.key,
                  $event.target.value
                )
              "
            >
              <option value="">
                {{ t('ONIX.CANVAS.NOT_CONNECTED') }}
              </option>
              <option
                v-for="node in otherNodes"
                :key="node.id"
                :value="node.id"
              >
                {{ node.label }}
              </option>
            </select>
            <Button
              :label="t('ONIX.CANVAS.REMOVE')"
              size="sm"
              color="slate"
              variant="faded"
              :disabled="!isAdmin"
              @click="removeBranch(index)"
            />
          </div>
          <Button
            :label="t('ONIX.CANVAS.ADD_BRANCH')"
            size="sm"
            color="slate"
            class="mb-3"
            :disabled="!isAdmin"
            @click="addBranch"
          />
        </template>

        <template v-else-if="draftType === 'instruct'">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.INSTRUCTION') }}
          </label>
          <textarea
            v-model="draftInstruction"
            rows="5"
            class="mb-3"
            :class="[areaClass]"
            :disabled="!isAdmin"
          />
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.GUARDRAILS') }}
          </label>
          <textarea
            v-model="draftGuardrails"
            rows="4"
            class="mb-3"
            :class="[areaClass]"
            :disabled="!isAdmin"
            :placeholder="t('ONIX.CANVAS.GUARDRAILS_PLACEHOLDER')"
          />
        </template>

        <template v-else-if="draftType === 'retrieve'">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.COLLECTION') }}
          </label>
          <select
            v-model="draftCollectionId"
            class="mb-3"
            :class="[fieldClass]"
            :disabled="!isAdmin"
          >
            <option value="">
              {{ t('ONIX.CANVAS.NO_COLLECTION') }}
            </option>
            <option
              v-for="collection in collections"
              :key="collection.id"
              :value="collection.id"
            >
              {{ collection.name }}
            </option>
          </select>
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.TOP_K') }}
          </label>
          <input
            v-model.number="draftTopK"
            type="number"
            min="1"
            max="20"
            class="mb-3"
            :class="[fieldClass]"
            :disabled="!isAdmin"
          />
        </template>

        <template v-else-if="draftType === 'agent'">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.MAX_ROUNDS') }}
          </label>
          <input
            v-model.number="draftMaxRounds"
            type="number"
            min="1"
            max="8"
            class="mb-3"
            :class="[fieldClass]"
            :disabled="!isAdmin"
          />
          <p class="text-xs text-n-slate-11 mb-2">
            {{ t('ONIX.CANVAS.VERIFIED_ACTIONS') }}
          </p>
          <ul class="m-0 p-0 list-none flex flex-col gap-1 mb-3">
            <li v-for="action in verifiedActions" :key="action.id">
              <label class="flex items-center gap-2 text-sm text-n-slate-12">
                <input
                  type="checkbox"
                  :checked="draftActionIds.includes(action.id)"
                  :disabled="!isAdmin"
                  @change="toggleAction(action.id)"
                />
                {{ action.title }}
              </label>
            </li>
            <li v-if="!verifiedActions.length" class="text-xs text-n-slate-11">
              {{ t('ONIX.CANVAS.NO_VERIFIED_ACTIONS') }}
            </li>
          </ul>
        </template>

        <template v-else-if="draftType === 'respond'">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.MESSAGE') }}
          </label>
          <textarea
            v-model="draftContent"
            rows="5"
            class="mb-3"
            :class="[areaClass]"
            :disabled="!isAdmin"
          />
          <label class="flex items-center gap-2 mb-2 text-sm text-n-slate-12">
            <input v-model="draftAwait" type="checkbox" :disabled="!isAdmin" />
            {{ t('ONIX.CANVAS.AWAIT') }}
          </label>
          <label v-if="draftAwait" class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.RESUME_NODE') }}
          </label>
          <select
            v-if="draftAwait"
            v-model="draftResumeNode"
            class="mb-3"
            :class="[fieldClass]"
            :disabled="!isAdmin"
          >
            <option value="">
              {{ t('ONIX.CANVAS.NOT_CONNECTED') }}
            </option>
            <option v-for="item in nodes" :key="item.id" :value="item.id">
              {{ item.label }}
            </option>
          </select>
        </template>

        <template v-else-if="draftType === 'actibid'">
          <label class="block text-xs text-n-slate-11 mb-1">
            {{ t('ONIX.CANVAS.ACTIBID_OPERATION') }}
          </label>
          <select
            v-model="draftOperation"
            class="mb-3"
            :class="[fieldClass]"
            :disabled="!isAdmin"
          >
            <option value="detail">
              {{ t('ONIX.CANVAS.ACTIBID_DETAIL') }}
            </option>
            <option value="search">
              {{ t('ONIX.CANVAS.ACTIBID_SEARCH') }}
            </option>
          </select>
          <p class="text-xs text-n-slate-11 mb-3">
            {{ t('ONIX.CANVAS.HELP_ACTIBID') }}
          </p>
        </template>

        <p
          v-else-if="draftType === 'extract' || draftType === 'compute'"
          class="text-xs text-n-slate-11 mb-3"
        >
          {{ t(`ONIX.CANVAS.HELP_${draftType.toUpperCase()}`) }}
        </p>

        <div class="flex flex-col gap-2">
          <section v-if="showsEffects" class="mb-3 pt-3 border-t border-n-weak">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
              {{ t('ONIX.EFFECTS.TITLE') }}
            </h3>
            <p class="text-xs text-n-slate-11 mb-3">
              {{ t('ONIX.EFFECTS.HINT') }}
            </p>

            <p class="text-xs text-n-slate-11 mb-1">
              {{ t('ONIX.EFFECTS.LABELS') }}
            </p>
            <ul
              class="m-0 mb-3 p-2 list-none flex flex-col gap-1 max-h-32 overflow-auto rounded-lg border border-n-weak"
            >
              <li v-for="label in accountLabels" :key="label.id || label.title">
                <label class="flex items-center gap-2 text-sm text-n-slate-12">
                  <input
                    type="checkbox"
                    :checked="draftEffects.labelTitles.includes(label.title)"
                    :disabled="!isAdmin"
                    @change="
                      draftEffects.labelTitles = toggleIn(
                        draftEffects.labelTitles,
                        label.title
                      )
                    "
                  />
                  {{ label.title }}
                </label>
              </li>
              <li v-if="!accountLabels.length" class="text-xs text-n-slate-11">
                {{ t('ONIX.EFFECTS.NO_LABELS') }}
              </li>
            </ul>

            <label class="block text-xs text-n-slate-11 mb-1">
              {{ t('ONIX.EFFECTS.TEAM') }}
            </label>
            <select
              v-model="draftEffects.teamId"
              class="mb-3"
              :class="[fieldClass]"
              :disabled="!isAdmin"
            >
              <option value="">
                {{ t('ONIX.EFFECTS.NO_TEAM') }}
              </option>
              <option
                v-for="team in accountTeams"
                :key="team.id"
                :value="String(team.id)"
              >
                {{ team.name }}
              </option>
            </select>

            <label class="block text-xs text-n-slate-11 mb-1">
              {{ t('ONIX.EFFECTS.PRIORITY') }}
            </label>
            <select
              v-model="draftEffects.priority"
              class="mb-3"
              :class="[fieldClass]"
              :disabled="!isAdmin"
            >
              <option value="">
                {{ t('ONIX.EFFECTS.PRIORITY_NONE') }}
              </option>
              <option value="low">
                {{ t('ONIX.EFFECTS.PRIORITY_LOW') }}
              </option>
              <option value="medium">
                {{ t('ONIX.EFFECTS.PRIORITY_MEDIUM') }}
              </option>
              <option value="high">
                {{ t('ONIX.EFFECTS.PRIORITY_HIGH') }}
              </option>
              <option value="urgent">
                {{ t('ONIX.EFFECTS.PRIORITY_URGENT') }}
              </option>
            </select>

            <label class="block text-xs text-n-slate-11 mb-1">
              {{ t('ONIX.EFFECTS.NOTE') }}
            </label>
            <textarea
              v-model="draftEffects.privateNote"
              rows="3"
              class="mb-3"
              :class="[areaClass]"
              :disabled="!isAdmin"
            />

            <label class="flex items-center gap-2 mb-2 text-sm text-n-slate-12">
              <input
                v-model="draftEffects.resolve"
                type="checkbox"
                :disabled="!isAdmin"
              />
              {{ t('ONIX.EFFECTS.RESOLVE') }}
            </label>
            <label class="flex items-center gap-2 mb-3 text-sm text-n-slate-12">
              <input
                v-model="draftEffects.handoff"
                type="checkbox"
                :disabled="!isAdmin"
              />
              {{ t('ONIX.EFFECTS.HANDOFF') }}
            </label>

            <template v-if="draftType === 'instruct'">
              <label
                class="flex items-center gap-2 mb-2 text-sm text-n-slate-12"
              >
                <input
                  v-model="draftEffects.allowModel"
                  type="checkbox"
                  :disabled="!isAdmin"
                />
                {{ t('ONIX.EFFECTS.ALLOW_MODEL') }}
              </label>
              <p class="text-xs text-n-slate-11 mb-2">
                {{ t('ONIX.EFFECTS.ALLOW_MODEL_HINT') }}
              </p>
              <template v-if="draftEffects.allowModel">
                <ul
                  class="m-0 mb-3 p-2 list-none flex flex-col gap-1 max-h-32 overflow-auto rounded-lg border border-n-weak"
                >
                  <li
                    v-for="label in accountLabels"
                    :key="`allow-${label.id || label.title}`"
                  >
                    <label
                      class="flex items-center gap-2 text-sm text-n-slate-12"
                    >
                      <input
                        type="checkbox"
                        :checked="
                          draftEffects.allowLabels.includes(label.title)
                        "
                        :disabled="!isAdmin"
                        @change="
                          draftEffects.allowLabels = toggleIn(
                            draftEffects.allowLabels,
                            label.title
                          )
                        "
                      />
                      {{ label.title }}
                    </label>
                  </li>
                </ul>
                <ul
                  class="m-0 mb-3 p-2 list-none flex flex-col gap-1 max-h-32 overflow-auto rounded-lg border border-n-weak"
                >
                  <li
                    v-for="team in accountTeams"
                    :key="`allow-team-${team.id}`"
                  >
                    <label
                      class="flex items-center gap-2 text-sm text-n-slate-12"
                    >
                      <input
                        type="checkbox"
                        :checked="
                          draftEffects.allowTeamIds.includes(Number(team.id))
                        "
                        :disabled="!isAdmin"
                        @change="
                          draftEffects.allowTeamIds = toggleIn(
                            draftEffects.allowTeamIds,
                            Number(team.id)
                          )
                        "
                      />
                      {{ team.name }}
                    </label>
                  </li>
                </ul>
              </template>
            </template>
          </section>

          <Button
            :label="t('ONIX.CANVAS.APPLY')"
            size="sm"
            class="w-full"
            :disabled="!isAdmin"
            @click="applyNodeEdits"
          />
          <Button
            :label="t('ONIX.CANVAS.DELETE_NODE')"
            size="sm"
            color="ruby"
            variant="faded"
            class="w-full"
            :disabled="!isAdmin"
            @click="deleteSelectedNode"
          />
        </div>
      </section>
    </aside>
  </div>
</template>
